import { logFatal } from '@core/logging';
import { useSiteSettingsContext } from '@core/shared';
import { BroadcastQuality } from '@core/types';
import { Button, Input, LinkIcon, Loader, Select, Switch } from '@core/ui';
import {
    isExternalEncoder,
    resolveIngestType,
    refreshRtmpIngestInformation,
    refreshSrtIngestInformation,
    setBroadcastQuality,
    setMoqPublisher,
    useAppDispatch,
    isChromiumBrowser,
    useAppSelector,
    writePreferClassicConnection,
    MOQ_IS_DEFAULT
} from '@online-webinar/store';
import { DateTime } from 'luxon';
import { lazy, RefObject, Suspense, useEffect, useId, useRef, useState } from 'react';
import { getI18n, Trans, useTranslation } from 'react-i18next';
import { ExternalIngestStillOpenWarning } from '../../atoms';
import { serializeError } from 'serialize-error';
import { VolumeMeter } from '../';
import { useAudioStream, useVideoStream } from '../../../hooks';
import { Popup } from '../../atoms';
const BroadcastVideoPreview = lazy(() => import('../../organism/BroadcastVideoPreview'));

enum AudioAndVideoMixerStatus {
    Initializing,
    Ready,
    Error
}

interface Props {
    disableExternalIngest?: boolean;
    disableMoq?: boolean;
    onAudioVideoSelect: (audioDeviceId: string, videoDeviceId: string) => void;
}

export const AudioAndVideoSelection = ({ onAudioVideoSelect, disableExternalIngest, disableMoq }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const srtUrl = useAppSelector((c) => c.liveWebinar?.srtIngestInformation?.baseUrl);
    const srtStreamId = useAppSelector((c) => c.liveWebinar?.srtIngestInformation?.streamKey);
    const rtmpUrl = useAppSelector((c) => c.liveWebinar?.rtmpIngestInformation?.url);
    const rtmpBaseUrl = useAppSelector((c) => c.liveWebinar?.rtmpIngestInformation?.baseUrl);
    const rtmpStreamKey = useAppSelector((c) => c.liveWebinar?.rtmpIngestInformation?.streamKey);
    const earlyStartOn = useAppSelector((c) => c.liveWebinar?.earlyStartOn);
    const endOn = useAppSelector((c) => c.liveWebinar?.endOn);
    const vindralChannel = useAppSelector((c) => c.liveWebinar?.vindralChannel);
    const hadExternalIngestBefore = useAppSelector((c) => c.liveWebinar?.hadExternalIngest);
    // Selecting RTMP or SRT hands the presenter their ingest URL immediately, so they can have an
    // encoder publishing before they ever press Continue. The store flag only learns about a
    // confirmed selection, so remember it here too or that exact case would go unwarned.
    const [pickedExternalIngest, setPickedExternalIngest] = useState(false);
    const moqPublisher = useAppSelector((c) => c.liveWebinar?.moqPublisher);
    const broadcastQuality = useAppSelector((c) => c.liveWebinar?.broadcastQuality);
    const { defaultBroadcastQuality } = useSiteSettingsContext();
    const effectiveQuality = broadcastQuality ?? defaultBroadcastQuality;

    const [status, setStatus] = useState(AudioAndVideoMixerStatus.Initializing);

    const [audioDeviceId, setAudioDeviceId] = useState<string | null>(null);
    const [audioDeviceIds, setAudioDeviceIds] = useState<MediaDeviceInfo[]>([]);

    const [videoDeviceId, setVideoDeviceId] = useState<string | null>(null);
    const [videoDeviceIds, setVideoDeviceIds] = useState<MediaDeviceInfo[]>([]);

    const ingestType = resolveIngestType(videoDeviceId, moqPublisher);
    const hadExternalIngest = hadExternalIngestBefore || pickedExternalIngest;

    const { audioStream, audioQuality, hasAudioError, setAudioDeviceId: setAudioDeviceIdForStream } = useAudioStream();
    const { videoStream, videoQuality, hasVideoError, setVideoDeviceId: setVideoDeviceIdForStream } = useVideoStream();

    const videoRef: RefObject<HTMLVideoElement | null> = useRef<HTMLVideoElement>(null);

    // Hidden while MoQ is not the default: the switch and its text describe opting out of MoQ.
    const canUseMoq = MOQ_IS_DEFAULT && isChromiumBrowser();

    const isFirefox = /firefox/i.test(navigator.userAgent);
    // Vindral supports neither browser transport in Firefox: WebRTC ingest is broken there and won't be
    // fixed (it is deprecated in favour of MoQ), and MoQ seems to publish but Vindral does not support
    // it. A Firefox presenter can only go live through an external encoder.
    const isWebcamUnavailable = isFirefox && !!videoDeviceId && !isExternalEncoder(ingestType);
    const webcamUnavailableId = useId();

    useEffect(() => {
        setVideoDeviceIdForStream(videoDeviceId);
        if (videoDeviceId === 'rtmp' || videoDeviceId === 'srt') {
            setAudioDeviceId(videoDeviceId);
        }
        if (videoDeviceId === 'rtmp' && !rtmpUrl && vindralChannel) {
            dispatch(refreshRtmpIngestInformation());
        }
        if (videoDeviceId === 'srt' && !srtUrl && vindralChannel) {
            dispatch(refreshSrtIngestInformation());
        }
    }, [videoDeviceId, vindralChannel, rtmpUrl, srtUrl]);

    useEffect(() => {
        if (isExternalEncoder(ingestType)) setPickedExternalIngest(true);
    }, [ingestType]);

    useEffect(() => {
        setAudioDeviceIdForStream(audioDeviceId);
    }, [audioDeviceId]);

    useEffect(() => {
        if (broadcastQuality == null) {
            dispatch(setBroadcastQuality(defaultBroadcastQuality));
        }
    }, [broadcastQuality, defaultBroadcastQuality]);

    useEffect(() => {
        if (videoDeviceId === 'rtmp' || videoDeviceId === 'srt') {
            if (audioDeviceId !== videoDeviceId) {
                setAudioDeviceId(videoDeviceId);
            }
        } else if (audioDeviceId === 'rtmp' || audioDeviceId === 'srt') {
            if (videoDeviceId) {
                setAudioDeviceId(audioDeviceIds.length > 0 ? audioDeviceIds[0].deviceId : null);
            } else {
                setAudioDeviceId(null);
            }
        }
    }, [audioDeviceId, videoDeviceId, audioDeviceIds]);

    const refreshDevices = async () => {
        setStatus(AudioAndVideoMixerStatus.Initializing);

        try {
            let stream: MediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    autoGainControl: { ideal: false },
                    channelCount: { ideal: 2 },
                    echoCancellation: { ideal: false },
                    noiseSuppression: { ideal: false },
                    sampleRate: { ideal: 48000 },
                    sampleSize: { ideal: 16 }
                },
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    frameRate: { ideal: 30 }
                }
            });

            // Stop the stream immediately, we don't need/want it - but a call go gum is required for enumerateDevices to work
            // We do track which device was the default device, so we can set it
            let audioDevice = '';
            let videoDevice = '';
            stream.getTracks().forEach((track) => {
                if (track.kind === 'audio') {
                    audioDevice = track.getSettings().deviceId ?? '';
                }
                if (track.kind === 'video') {
                    videoDevice = track.getSettings().deviceId ?? '';
                }
                track.stop();
            });

            const devices: MediaDeviceInfo[] = await navigator.mediaDevices.enumerateDevices();

            setAudioDeviceId(audioDevice);
            setVideoDeviceId(videoDevice);

            const audioDevices: MediaDeviceInfo[] = [];
            const videoDevices: MediaDeviceInfo[] = [];
            for (const device of devices) {
                if (device.kind === 'audioinput') {
                    audioDevices.push(device);
                } else if (device.kind === 'videoinput') {
                    videoDevices.push(device);
                }
            }
            setAudioDeviceIds(audioDevices);
            setVideoDeviceIds(videoDevices);
            setStatus(AudioAndVideoMixerStatus.Ready);
        } catch (error) {
            logFatal('Error initializing audio and video devices', { Error: serializeError(error) });

            setVideoDeviceId('rtmp');
            setAudioDeviceId('rtmp');
            setAudioDeviceIds([]);
            setVideoDeviceIds([]);
            setStatus(AudioAndVideoMixerStatus.Error);
            return;
        }
    };

    useEffect(() => {
        refreshDevices();
    }, []);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.srcObject = videoStream;
        }
    }, [videoStream]);

    const [copyButtonTitle, setCopyButtonTitle] = useState('');
    const copyIngestLink = (url: string) => {
        setCopyButtonTitle(t('Share.CopiedLink'));
        navigator.clipboard.writeText(`${url}`);
    };

    return (
        <Popup
            title={t('Broadcaster.AudioAndVideoSelect.Heading')}
            hasWarning={status === AudioAndVideoMixerStatus.Error || hasAudioError || hasVideoError}
            open={true}
            onClose={() => {}}
            onConfirm={() => {}}
            isCloseFunctionality={false}
        >
            <div className="w-full">
                {status === AudioAndVideoMixerStatus.Initializing && <div>{t('Video.Initializing')}</div>}
                {status === AudioAndVideoMixerStatus.Ready && (
                    <>
                        <Select
                            name="videoDevice"
                            label={t('Broadcaster.AudioAndVideoSelect.VideoSelect.label')}
                            value={videoDeviceId ?? ''}
                            onChange={(e) => setVideoDeviceId(e.target.value)}
                        >
                            <option key="" value="">
                                {t('Broadcaster.AudioAndVideoSelect.VideoSelect.label')}
                            </option>
                            {!disableExternalIngest && (
                                <option key="rtmp" value="rtmp">
                                    {t('Broadcaster.AudioAndVideoSelect.VideoSelect.RTMP')}
                                </option>
                            )}
                            {!disableExternalIngest && (
                                <option key="srt" value="srt">
                                    {t('Broadcaster.AudioAndVideoSelect.VideoSelect.SRT')}
                                </option>
                            )}
                            {videoDeviceIds.map((device) => (
                                <option key={device.deviceId} value={device.deviceId}>
                                    {device.label}
                                </option>
                            ))}
                        </Select>
                        {hadExternalIngest && !isExternalEncoder(ingestType) && <ExternalIngestStillOpenWarning />}
                        {isWebcamUnavailable && (
                            <p id={webcamUnavailableId} data-testid="firefox-webcam-unavailable" className="bg-warning-light rounded-lg mb-6 p-2 text-sm">
                                {t('Broadcaster.AudioAndVideoSelect.FirefoxWebcamUnavailable')}
                            </p>
                        )}
                        {videoDeviceId !== 'rtmp' && videoDeviceId !== 'srt' && (
                            <Select
                                name="audioDevice"
                                label={t('Broadcaster.AudioAndVideoSelect.AudioSelect.label')}
                                value={audioDeviceId ?? ''}
                                onChange={(e) => setAudioDeviceId(e.target.value)}
                            >
                                {audioDeviceIds.length > 0 && (
                                    <>
                                        <option key="" value="">
                                            {t('Broadcaster.AudioAndVideoSelect.AudioSelect.label')}
                                        </option>
                                        {audioDeviceIds.map((device) => (
                                            <option key={device.deviceId} value={device.deviceId}>
                                                {device.label}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </Select>
                        )}
                        {videoDeviceId !== 'rtmp' && videoDeviceId !== 'srt' && (
                            <>
                                <Select
                                    name="broadcastQuality"
                                    label={t('Broadcaster.AudioAndVideoSelect.Quality.label')}
                                    value={effectiveQuality}
                                    classNames="mb-6"
                                    onChange={(e) => dispatch(setBroadcastQuality(e.target.value as BroadcastQuality))}
                                >
                                    <option value={BroadcastQuality.High}>{t('Broadcaster.AudioAndVideoSelect.Quality.High')}</option>
                                    <option value={BroadcastQuality.Medium}>{t('Broadcaster.AudioAndVideoSelect.Quality.Medium')}</option>
                                    <option value={BroadcastQuality.Low}>{t('Broadcaster.AudioAndVideoSelect.Quality.Low')}</option>
                                </Select>
                                {effectiveQuality === BroadcastQuality.High && (
                                    <p className="bg-warning-light rounded-lg mb-6 p-2 text-sm">
                                        <Trans
                                            components={{ strong: <strong className="font-medium" /> }}
                                            i18nKey="Broadcaster.AudioAndVideoSelect.Quality.HighWarning"
                                        />
                                    </p>
                                )}
                            </>
                        )}
                        {!disableExternalIngest && !disableMoq && canUseMoq && videoDeviceId !== 'rtmp' && videoDeviceId !== 'srt' && (
                            <>
                                {/* MoQ is the default here; the switch opts out, and only this choice is remembered per browser. */}
                                <Switch
                                    label={t('Broadcaster.AudioAndVideoSelect.UseClassicConnection.Label')}
                                    checked={moqPublisher === false}
                                    onChange={(e) => {
                                        writePreferClassicConnection(e.target.checked);
                                        dispatch(setMoqPublisher(!e.target.checked));
                                    }}
                                />
                                <p className="mt-2 mb-6 text-sm">{t('Broadcaster.AudioAndVideoSelect.UseClassicConnection.InfoText')}</p>
                            </>
                        )}
                    </>
                )}
                {status === AudioAndVideoMixerStatus.Error && (
                    <p>
                        <Trans i18nKey={'Broadcaster.AudioAndVideoSelection.NoDevicesError'} />
                    </p>
                )}

                {hasAudioError && (
                    <p>
                        <Trans i18nKey={'Broadcaster.AudioAndVideoSelection.AudioError'} />
                    </p>
                )}

                {hasVideoError && (
                    <p>
                        <Trans i18nKey={'Broadcaster.AudioAndVideoSelection.VideoError'} />
                    </p>
                )}

                {(audioStream || videoStream) && (
                    <div>
                        <div className="relative h-32 aspect-video bg-black">
                            {audioStream && <VolumeMeter audioStream={audioStream} />}
                            {videoStream && <video className="h-full mx-auto" autoPlay ref={videoRef} />}
                        </div>
                        <p className="text-xs">
                            {audioQuality && <span>Audio quality: {audioQuality}</span>}
                            {audioQuality && videoQuality && <span className="mx-1">|</span>}
                            {videoQuality && <span>Video quality: {videoQuality}</span>}
                        </p>
                    </div>
                )}

                {videoDeviceId === 'rtmp' && vindralChannel && (
                    <>
                        {!rtmpUrl && <Loader classNames="m-auto mb-4" />}
                        {rtmpUrl && rtmpBaseUrl && rtmpStreamKey && (
                            <>
                                <Input label={t('Broadcaster.AudioAndVideoSelect.RTMP.Link')} placeholder="RTMP URL" readOnly value={rtmpUrl} classNames="mb-4">
                                    <Button type="button" iconbtn onClick={() => copyIngestLink(rtmpUrl)}>
                                        <LinkIcon classNames="size-6 flex-none stroke-primary" />
                                        <span className="sr-only">{t('Share.Button.CopyLink')}</span>
                                    </Button>
                                </Input>
                                <Input
                                    label={t('Broadcaster.AudioAndVideoSelect.RTMP.BaseLink')}
                                    placeholder="RTMP URL"
                                    readOnly
                                    value={rtmpBaseUrl}
                                    classNames="mb-4"
                                >
                                    <Button type="button" iconbtn onClick={() => copyIngestLink(rtmpBaseUrl)}>
                                        <LinkIcon classNames="size-6 flex-none stroke-primary" />
                                        <span className="sr-only">{t('Share.Button.CopyLink')}</span>
                                    </Button>
                                </Input>
                                <Input
                                    label={t('Broadcaster.AudioAndVideoSelect.RTMP.StreamKey')}
                                    placeholder="RTMP URL"
                                    readOnly
                                    value={rtmpStreamKey}
                                    classNames="mb-2"
                                >
                                    <Button type="button" iconbtn onClick={() => copyIngestLink(rtmpStreamKey)}>
                                        <LinkIcon classNames="size-6 flex-none stroke-primary" />
                                        <span className="sr-only">{t('Share.Button.CopyLink')}</span>
                                    </Button>
                                </Input>
                                <p className="text-primary mb-4">{copyButtonTitle}</p>
                            </>
                        )}
                    </>
                )}

                {videoDeviceId === 'srt' && vindralChannel && (
                    <>
                        {!srtUrl && <Loader classNames="m-auto mb-4" />}
                        {srtUrl && srtStreamId && (
                            <>
                                <Input label={t('Broadcaster.AudioAndVideoSelect.SRT.Link')} placeholder="SRT URL" readOnly value={srtUrl} classNames="mb-2">
                                    <Button type="button" iconbtn onClick={() => copyIngestLink(srtUrl)}>
                                        <LinkIcon classNames="size-6 stroke-primary" />
                                        <span className="sr-only">{t('Share.Button.CopyLink')}</span>
                                    </Button>
                                </Input>
                                <Input
                                    label={t('Broadcaster.AudioAndVideoSelect.SRT.StreamId')}
                                    placeholder="SRT Stream ID"
                                    readOnly
                                    value={srtStreamId}
                                    classNames="mb-2"
                                >
                                    <Button type="button" iconbtn onClick={() => copyIngestLink(srtStreamId)}>
                                        <LinkIcon classNames="size-6 flex-none stroke-primary" />
                                        <span className="sr-only">{t('Share.Button.CopyLink')}</span>
                                    </Button>
                                </Input>
                                <p className="text-primary mb-4">{copyButtonTitle}</p>
                            </>
                        )}
                    </>
                )}

                {(videoDeviceId === 'rtmp' || videoDeviceId === 'srt') && !vindralChannel && earlyStartOn && endOn && (
                    <p>
                        <Trans
                            i18nKey={'Broadcaster.AudioAndVideoSelect.IngestAvailability'}
                            values={{
                                timeslotstart: DateTime.fromISO(earlyStartOn).toLocal().setLocale(getI18n().language).toFormat('DDD t'),
                                timeslotend: DateTime.fromISO(endOn).toLocal().setLocale(getI18n().language).toFormat('DDD t')
                            }}
                        />
                    </p>
                )}

                {(videoDeviceId === 'rtmp' || videoDeviceId === 'srt') && vindralChannel && (
                    <div className={`relative h-32 aspect-video bg-black`}>
                        <Suspense fallback={<Loader classNames="m-auto" />}>
                            <BroadcastVideoPreview previewOnly />
                        </Suspense>
                    </div>
                )}

                {audioDeviceId && videoDeviceId && (
                    <div className="pt-8">
                        <Button
                            variant="primary"
                            data-testid="audio-video-continue"
                            aria-disabled={isWebcamUnavailable}
                            aria-describedby={isWebcamUnavailable ? webcamUnavailableId : undefined}
                            onClick={() => onAudioVideoSelect(audioDeviceId!, videoDeviceId!)}
                            classNames="w-full"
                        >
                            {t('Generic.Button.Continue')}
                        </Button>
                    </div>
                )}
            </div>
        </Popup>
    );
};
